import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isToday,
  isYesterday,
  Locale,
  setDefaultOptions,
} from "date-fns";
import { de, es, et, fi, fr, ja, nb, pt, ptBR } from "date-fns/locale";
import { createContext, ReactNode, useCallback, useEffect } from "react";

import { useAppProperties, useTranslations } from "@/hooks";
import { getDateFromUTCString } from "@/utils";

const DEFAULT_LANGUAGE = "English";

const LOCALE_MAP: Record<string, Locale> = {
  French: fr,
  Finnish: fi,
  German: de,
  Spanish: es,
  Norwegian: nb,
  Japanese: ja,
  Estonian: et,
  Portuguese: pt,
  "Portuguese (Brazil)": ptBR,
};

interface ContextValue {
  translate: (key: string) => string;
  translationsIsLoaded: boolean;
  translationsIsError: boolean;
  formatDate: (date: Date, includeYear?: boolean) => string;
  formatDetailedDatetime: (date: Date) => string;
  formatRelativeDateFromString: (dateString: string) => string;
}

type OptionalContextValue = ContextValue | undefined;

const TranslateContext = createContext<OptionalContextValue>(undefined);
const TranslateProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useAppProperties();
  const language = data?.Language ?? DEFAULT_LANGUAGE;

  // Set the locale for date-fns
  useEffect(() => {
    if (language in LOCALE_MAP) {
      const locale = LOCALE_MAP[language];
      setDefaultOptions({ locale });
    }
  }, [language]);

  const { translations, isError } = useTranslations({
    language,
    refetchOnMount: "always",
    enabled: !!data,
  });

  const translate = useCallback(
    (key: string) => {
      if (!translations || isError) {
        return key;
      }

      return translations[key] || key;
    },
    [translations, isError],
  );

  const formatDate = useCallback(
    (date: Date, includeYear?: boolean) => {
      const dateFormat = language === "Japanese" ? "MMMM do" : "d MMMM";
      return format(date, `${dateFormat}${includeYear ? ", yyyy" : ""}`);
    },
    [language],
  );

  const formatDetailedDatetime = useCallback(
    (date: Date) => {
      const time = format(date, "h:mmaaa")
        .replace("am", "AM") // Capitalize
        .replace("pm", "PM");

      if (isYesterday(date)) {
        return `${translate("Yesterday")}, ${time}`;
      }
      if (isToday(date)) {
        return `${translate("Today")}, ${time}`;
      }

      return `${formatDate(date)}, ${translate("at")} ${time}`;
    },
    [translate, formatDate],
  );

  const formatRelativeDate = useCallback(
    (date: Date) => {
      const now = new Date();
      const minutesDiff = differenceInMinutes(now, date);
      if (minutesDiff < 1) {
        return translate("Just Now");
      }

      if (minutesDiff < 60) {
        const minutes = translate("minutes").toLowerCase();
        return `${minutesDiff}${minutes[0]}`;
      }

      const hoursDiff = differenceInHours(now, date);
      const hours = translate("hours").toLowerCase();
      if (hoursDiff < 24) {
        return `${hoursDiff}${hours[0]}`;
      }

      const daysDiff = differenceInDays(now, date);
      if (daysDiff < 3) {
        const days = translate("days").toLowerCase();
        return `${daysDiff}${days[0]}`;
      }

      const dateFormat = language === "Japanese" ? "MMM do yyyy" : "d MMM yy";

      return format(date, dateFormat);
    },
    [translate, language],
  );

  // Format date like 22 MAR 24 in local timezone
  const formatRelativeDateFromString = useCallback(
    (dateString: string) => {
      try {
        const date = getDateFromUTCString(dateString);
        return date ? formatRelativeDate(date) : "";
      } catch (e) {
        return "";
      }
    },
    [formatRelativeDate],
  );

  return (
    <TranslateContext.Provider
      value={{
        translate,
        translationsIsLoaded: !!translations,
        translationsIsError: isError,
        formatDate,
        formatDetailedDatetime,
        formatRelativeDateFromString,
      }}
    >
      {children}
    </TranslateContext.Provider>
  );
};
export { TranslateContext, TranslateProvider };
